/**
 * Read-only widget grid.
 *
 * Used by BOTH the builder preview and the published viewer, so what an admin
 * previews is literally what an end user gets — there is no second rendering
 * path that could drift.
 *
 * RESPONSIVE: below `STACK_BREAKPOINT` the 12-column grid collapses to a single
 * column in `y` order, because a 3-column-wide KPI is unreadable on a phone.
 * Width is measured from the CONTAINER, not the window, so the grid also
 * collapses correctly when it sits beside a builder sidebar.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WidgetRenderer from './WidgetRenderer';
import {
  GRID_COLUMNS,
  GRID_GAP,
  GRID_ROW_HEIGHT,
  fitRowHeight,
  nextFreeRow,
} from '../utils/dashboardUtils';
import { signalsFor, type ActionState } from '../utils/actionRuntime';
import type {
  ActionSignal,
  DashboardDataSource,
  DashboardWidget,
} from '../types/dashboardTypes';

const STACK_BREAKPOINT = 720;

interface Props {
  widgets: DashboardWidget[];
  dataSources: DashboardDataSource[];
  refreshInterval: number;
  accentColor: string;
  /** Dashboard theme, forwarded so a map widget can pick a matching basemap. */
  themeMode?: 'light' | 'dark';
  /** Dashboard-wide default widget border colour — see `DashboardTheme.dividerColor`. */
  dividerColor?: string;
  /** A `dataSourceId → WHERE` map contributed by the header's own selectors —
   *  see `selectorRuntime.ts`'s `selectorFiltersFor`. */
  selectorFilters?: Record<string, string>;
}

/**
 * Container size, so layout decisions do not depend on the window alone.
 *
 * Height matters as much as width now: the grid divides the height it is given
 * between its rows, which is what keeps the dashboard on one page.
 */
export function useContainerSize<T extends HTMLElement>() {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const observerRef = useRef<ResizeObserver | null>(null);

  /**
   * A CALLBACK ref, not an object ref with a mount effect.
   *
   * An effect with `[]` deps runs once, when the component mounts — which is
   * not necessarily when the measured element appears. A chart lives inside
   * WidgetFrame, and WidgetFrame renders a loading state instead of its
   * children while the first query is in flight: by the time the element
   * exists, the one-shot effect has already run against a null ref and the
   * size stays 0 forever. A callback ref fires whenever the node attaches or
   * detaches, so measurement starts the moment there is something to measure.
   */
  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box) setSize({ width: box.width, height: box.height });
    });
    observer.observe(node);
    observerRef.current = observer;

    const box = node.getBoundingClientRect();
    setSize({ width: box.width, height: box.height });
  }, []);

  /* Deliberately NO unmount effect that disconnects.
     StrictMode runs effect cleanups on a simulated unmount WITHOUT detaching
     refs, so such a cleanup kills the observer while the element is still
     mounted — the box then shrinks on screen while the measurement stays at
     its first value. Detach is already handled above: React calls this ref with
     null when the node goes away, and the observer dies with it. */

  return { ref, ...size };
}

export default function DashboardGrid({
  widgets,
  dataSources,
  refreshInterval,
  accentColor,
  themeMode,
  dividerColor,
  selectorFilters = {},
}: Props) {
  const { ref, width, height } = useContainerSize<HTMLDivElement>();

  /* Live action signals, keyed by the widget that raised them. Held here rather
     than in each widget because a signal is a property of the DASHBOARD: one
     click has to be visible to every other tile. */
  const [actionState, setActionState] = useState<ActionState>({});

  const emitSignal = useCallback((sourceId: string, signal: ActionSignal | null) => {
    setActionState((current) => {
      if (!signal) {
        if (!(sourceId in current)) return current;
        const next = { ...current };
        delete next[sourceId];
        return next;
      }
      return { ...current, [sourceId]: signal };
    });
  }, []);

  const clearSources = useCallback((sourceIds: string[]) => {
    setActionState((current) => {
      const next = { ...current };
      for (const id of sourceIds) delete next[id];
      return next;
    });
  }, []);

  /* A widget that no longer exists must not keep filtering the page. */
  const widgetIdKey = widgets.map((widget) => widget.id).join(',');
  useEffect(() => {
    const live = new Set(widgetIdKey.split(','));
    setActionState((current) => {
      const kept = Object.keys(current).filter((id) => live.has(id));
      return kept.length === Object.keys(current).length
        ? current
        : Object.fromEntries(kept.map((id) => [id, current[id]]));
    });
  }, [widgetIdKey]);

  /* Four resolutions, one per action kind. A map can be selected, zoomed and
     panned independently, so they cannot be collapsed into one list. */
  const signalsByWidget = useMemo(() => {
    const filter: Record<string, ActionSignal[]> = {};
    const select: Record<string, ActionSignal[]> = {};
    const zoom: Record<string, ActionSignal[]> = {};
    const pan: Record<string, ActionSignal[]> = {};
    for (const widget of widgets) {
      filter[widget.id] = signalsFor(widget, widgets, actionState, ['filter']);
      select[widget.id] = signalsFor(widget, widgets, actionState, ['select']);
      zoom[widget.id] = signalsFor(widget, widgets, actionState, ['zoom']);
      pan[widget.id] = signalsFor(widget, widgets, actionState, ['pan']);
    }
    return { filter, select, zoom, pan };
  }, [widgets, actionState]);
  const stacked = width > 0 && width < STACK_BREAKPOINT;

  const ordered = [...widgets].sort(
    (a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x,
  );

  /* Only the rows that actually hold something. `gridRowCount` adds spare rows
     for dropping into, which belongs in the builder — reserving them here would
     leave a permanent empty band at the bottom of a published dashboard. */
  const rows = Math.max(1, nextFreeRow(widgets));
  const rowHeight = height > 0 ? fitRowHeight(height, rows) : GRID_ROW_HEIGHT;

  if (widgets.length === 0) {
    return (
      <div ref={ref} className="flex h-full min-h-[240px] items-center justify-center">
        <p className="text-sm text-slate-400">This dashboard has no widgets yet.</p>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      /* Fills its parent, so `height` above is the real space available.
         Stacked (phone) mode is the one case that still scrolls: six widgets
         cannot be legible in a single phone screen, and shrinking them to fit
         would be worse than scrolling. */
      className={stacked ? 'h-full overflow-y-auto' : 'h-full overflow-hidden'}
      style={
        stacked
          ? { display: 'flex', flexDirection: 'column', gap: GRID_GAP }
          : {
              display: 'grid',
              gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${rows}, ${rowHeight}px)`,
              gap: GRID_GAP,
            }
      }
    >
      {ordered.map((widget) => (
        <div
          key={widget.id}
          style={
            stacked
              ? { height: widget.layout.h * GRID_ROW_HEIGHT, flexShrink: 0 }
              : {
                  gridColumn: `${widget.layout.x + 1} / span ${widget.layout.w}`,
                  gridRow: `${widget.layout.y + 1} / span ${widget.layout.h}`,
                  minWidth: 0,
                  minHeight: 0,
                }
          }
        >
          <WidgetRenderer
            widget={widget}
            dataSources={dataSources}
            refreshInterval={refreshInterval}
            accentColor={accentColor}
            themeMode={themeMode}
            dividerColor={dividerColor}
            selectorFilters={selectorFilters}
            incomingSignals={signalsByWidget.filter[widget.id] ?? []}
            selectSignals={signalsByWidget.select[widget.id] ?? []}
            zoomSignals={signalsByWidget.zoom[widget.id] ?? []}
            panSignals={signalsByWidget.pan[widget.id] ?? []}
            activeSignal={actionState[widget.id] ?? null}
            onSignal={emitSignal}
            onClearSignals={clearSources}
            // The map must re-measure when the tile's width changes.
            resizeToken={`${width}|${Math.round(rowHeight)}|${widget.layout.w}|${widget.layout.h}`}
          />
        </div>
      ))}
    </div>
  );
}
